import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

/* ------------------------------------------------------------------ */
/*  SVG icons for each account type                                    */
/* ------------------------------------------------------------------ */
const IconPerson = () => (
  <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
  </svg>
);

const IconBuilding = () => (
  <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
  </svg>
);

const IconPeople = () => (
  <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Checkmark + feature line                                           */
/* ------------------------------------------------------------------ */
function FeatureItem({ children }) {
  return (
    <li className="flex items-start gap-2.5">
      <span className="mt-0.5 flex-shrink-0 w-5 h-5 bg-primary-100 rounded-full flex items-center justify-center">
        <svg className="w-3 h-3 text-primary-600" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
        </svg>
      </span>
      <span className="text-body-sm text-surface-600">{children}</span>
    </li>
  );
}

/* ------------------------------------------------------------------ */
/*  Account type card data                                             */
/* ------------------------------------------------------------------ */
const accountTypes = [
  {
    type: 'user',
    title: 'User Account',
    description: 'Perfect for individuals who want to discover and participate in local events',
    icon: <IconPerson />,
    recommended: true,
    features: [
      'Discover local events near you',
      'Book tickets and RSVP instantly',
      'Save and share favorite events',
      'Connect with friends',
    ],
  },
  {
    type: 'business',
    title: 'Business Account',
    description: 'Ideal for businesses looking to host, promote, and monetize events',
    icon: <IconBuilding />,
    recommended: false,
    features: [
      'Create and manage events',
      'Promote your business',
      'Track event analytics & revenue',
      'Run promotions and discounts',
    ],
  },
  {
    type: 'organization',
    title: 'Organization Account',
    description: 'Designed for organizations and groups managing multiple events together',
    icon: <IconPeople />,
    recommended: false,
    features: [
      'Manage multiple events at scale',
      'Team collaboration tools',
      'Advanced reporting features',
      'Member management',
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */
const AccountTypeSelection = () => {
  const navigate = useNavigate();

  const handleAccountTypeSelect = (type) => {
    navigate('/register', { state: { accountType: type } });
  };

  return (
    <div className="min-h-screen bg-surface-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="text-center mb-12"
        >
          <h1 className="text-h1 sm:text-display text-surface-900">
            Choose Your Account Type
          </h1>
          <p className="mt-3 text-body-lg text-surface-500 max-w-xl mx-auto">
            Select the type of account that best fits your needs. You can always change this later.
          </p>
        </motion.div>

        {/* Cards */}
        <div className="grid gap-6 md:grid-cols-3">
          {accountTypes.map((account, index) => (
            <motion.div
              key={account.type}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.1 + index * 0.08 }}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
              whileTap={{ scale: 0.98 }}
              className="relative bg-white rounded-2xl shadow-soft-sm border border-surface-200 hover:shadow-soft-md transition-shadow duration-200 flex flex-col"
            >
              {/* Recommended badge */}
              {account.recommended && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center px-3 py-1 bg-primary-500 text-white text-caption font-semibold rounded-full shadow-colored">
                    Recommended
                  </span>
                </div>
              )}

              <div className="p-6 sm:p-8 flex flex-col flex-1">
                {/* Icon */}
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 ${
                  account.recommended ? 'bg-primary-100 text-primary-600' : 'bg-surface-100 text-surface-500'
                }`}>
                  {account.icon}
                </div>

                {/* Title & Description */}
                <h3 className="text-h3 text-surface-900 mb-2">{account.title}</h3>
                <p className="text-body-sm text-surface-500 mb-6">{account.description}</p>

                {/* Features */}
                <ul className="space-y-3 mb-8 flex-1">
                  {account.features.map((feature, i) => (
                    <FeatureItem key={i}>{feature}</FeatureItem>
                  ))}
                </ul>

                {/* CTA Button */}
                <button
                  onClick={() => handleAccountTypeSelect(account.type)}
                  className={`w-full py-3 px-4 text-body-sm font-semibold rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    account.recommended
                      ? 'bg-primary-500 text-white hover:bg-primary-600 focus:ring-primary-500/40 shadow-colored'
                      : 'bg-white text-surface-700 border border-surface-200 hover:bg-surface-50 hover:border-surface-300 focus:ring-surface-300/40'
                  }`}
                >
                  Select {account.title.split(' ')[0]}
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Footer help text */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.5 }}
          className="text-center text-body-sm text-surface-400 mt-10"
        >
          Not sure which to pick? Start with a User account -- you can upgrade anytime.
        </motion.p>
      </div>
    </div>
  );
};

export default AccountTypeSelection;
